import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiError } from 'api/core/ApiError';
import { ApiService } from 'api/services/ApiService';
import { DROPBOX_FOLDER_SYNC_QUERY_KEYS } from './useDropboxFolderSync.constants';

export interface SyncCompanyDocsSummary {
  imported_from_dropbox?: number;
  uploaded_to_dropbox?: number;
  skipped?: number;
  errors?: string[];
}

function formatSyncCompanyDocsErrorBody(body: unknown): string {
  if (!body || typeof body !== 'object') {
    return 'Failed to sync Company Docs.';
  }
  const { detail, message } = body as Record<string, unknown>;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map(String).join(' ');
  }
  if (typeof message === 'string') {
    return message;
  }
  return 'Failed to sync Company Docs.';
}

export function useSyncCompanyDocs(valuationId: string | undefined) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (cmdId: number) =>
      ApiService.apiDashboardOperationsCompanyMeasurementDatesSyncCompanyDocsCreate(String(cmdId)),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [DROPBOX_FOLDER_SYNC_QUERY_KEYS.COMPANY_MEASUREMENT_DATE_BY_ATTIO_VALUATION_ID, valuationId],
      });
      queryClient.invalidateQueries({ queryKey: ['documents'], exact: false });

      const summary = data as SyncCompanyDocsSummary;
      const imported = summary?.imported_from_dropbox ?? 0;
      const uploaded = summary?.uploaded_to_dropbox ?? 0;
      const skipped = summary?.skipped ?? 0;
      const errList = summary?.errors?.filter(Boolean) ?? [];
      const errSuffix = errList.length ? ` ${errList.join('; ')}` : '';
      const hasErr = errList.length > 0;
      enqueueSnackbar(
        `Company Docs synced: ${imported} imported from Dropbox, ${uploaded} uploaded to Dropbox, ${skipped} skipped.${errSuffix}`,
        { variant: hasErr ? 'warning' : 'success' }
      );
    },
    onError: (error: unknown) => {
      let message = 'Failed to sync Company Docs.';
      if (error instanceof ApiError) {
        message = formatSyncCompanyDocsErrorBody(error.body);
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
